/**
 * core/quests — the pure quest rules engine (owner decision D17).
 *
 * Deterministic, no IO, no rendering. Given the authored QuestDefs (data/quests),
 * the save's QuestState (progress only), and one QuestEvent emitted by an ordinary
 * play verb, `advanceQuests` folds the event into progress and reports which stages
 * cleared / which quests are ready to turn in. `offerableQuests` decides visibility
 * (soft gates only — never a wall). `acceptQuest` / `turnInQuest` mutate the
 * lifecycle immutably + idempotently.
 *
 * The no-pressure contract holds by construction here: nothing in this module can
 * ever produce a fail state, an expiry, or a decrement — progress is monotonic, a
 * non-matching event is a silent no-op, and a lost/aborted battle simply doesn't tick.
 *
 * Spec: notes/story-design/quest-system.md §4.
 */
import type {
  ObjectiveDef,
  ObjectiveProgress,
  QuestAdvance,
  QuestDef,
  QuestEvent,
  QuestProgress,
  QuestState,
} from '@/shared';

/** The read-only world facts availability gates may consult (visibility only). */
export interface QuestWorldSlice {
  readonly badges: readonly string[];
  readonly completedQuestIds: readonly string[];
  readonly caughtCount: number;
  /** Friendship with a giver/ref trainer, when known (soft gates only). */
  friendshipWith?(trainerId: string): number;
}

/** A tiny read-only species→type lookup so the engine stays pure + testable. */
export interface QuestLookup {
  /** Primary type slug for a species id (e.g. 'growlithe' → 'fire'), or undefined. */
  speciesType(id: string): string | undefined;
}

// ── Event → objective matching ──────────────────────────────────────────────────

/**
 * How much a single event advances one objective:
 *  - `null`  → the event is unrelated to this objective (no change).
 *  - number  → increment the running tally by this much (count objectives).
 *
 * Boolean objectives (catch-species, defeat-gym-leader, reach-level, visit-biome,
 * talk-to) report `1` on a match; their required count is treated as 1.
 */
function eventDelta(obj: ObjectiveDef, ev: QuestEvent, lookup: QuestLookup): number | null {
  switch (obj.kind) {
    case 'catch-species':
      return ev.kind === 'catch' && ev.speciesId === obj.speciesId ? 1 : null;

    case 'catch-type': {
      if (ev.kind !== 'catch') return null;
      // The caught mon may be dual-typed: the event carries one type, the lookup
      // resolves the (primary) dataset type. Match if EITHER is the target.
      const evType = ev.monType;
      const dexType = lookup.speciesType(ev.speciesId);
      return evType === obj.monType || dexType === obj.monType ? 1 : null;
    }

    case 'catch-count':
      return ev.kind === 'catch' ? 1 : null;

    case 'win-friendly-battles':
      // No-stakes wins: friendly OR gym battles tick (a gym win is also a friendly
      // measure per main-quest.md). Wild wins never count; a loss never ticks.
      return ev.kind === 'battle-win' && (ev.battleKind === 'friendly' || ev.battleKind === 'gym')
        ? 1
        : null;

    case 'defeat-gym-leader':
      return ev.kind === 'battle-win' && ev.battleKind === 'gym' && ev.gymId === obj.gymId
        ? 1
        : null;

    case 'reach-level':
      if (ev.kind !== 'level') return null;
      if (ev.level < obj.level) return null;
      if (obj.speciesId && ev.speciesId !== obj.speciesId) return null;
      return 1;

    case 'visit-biome':
      return ev.kind === 'visit-biome' && ev.biomeId === obj.biomeId ? 1 : null;

    case 'evolve':
      if (ev.kind !== 'evolve') return null;
      if (obj.toSpeciesId && ev.toSpeciesId !== obj.toSpeciesId) return null;
      return 1;

    case 'trade':
      if (ev.kind !== 'trade') return null;
      if (obj.withTrainerId && ev.withTrainerId !== obj.withTrainerId) return null;
      return 1;

    case 'show-off':
      if (ev.kind !== 'show-off') return null;
      if (obj.speciesId && ev.speciesId !== obj.speciesId) return null;
      return 1;

    case 'cast-move':
      if (ev.kind !== 'cast-move') return null;
      if (obj.moveId && ev.moveId !== obj.moveId) return null;
      if (obj.monType && ev.monType !== obj.monType) return null;
      return 1;

    case 'talk-to':
      return ev.kind === 'talk-to' && ev.trainerId === obj.trainerId ? 1 : null;

    default:
      return null;
  }
}

/** The count required to satisfy an objective (boolean objectives require 1). */
function requiredCount(obj: ObjectiveDef): number {
  switch (obj.kind) {
    case 'catch-type':
    case 'catch-count':
    case 'win-friendly-battles':
    case 'evolve':
    case 'trade':
    case 'show-off':
    case 'cast-move':
      return Math.max(1, obj.count);
    default:
      return 1; // boolean objectives
  }
}

/** Seed fresh (all-zero) progress for a stage's objectives. */
function seedStage(def: QuestDef, stageIndex: number): ObjectiveProgress[] {
  const stage = def.stages[stageIndex];
  if (!stage) return [];
  return stage.objectives.map((_, index) => ({ index, count: 0, done: false }));
}

// ── advanceQuests ─────────────────────────────────────────────────────────────

/**
 * Pure: fold one event into quest progress.
 *
 * For every ACTIVE quest that isn't fully complete, tick the matching objectives on
 * its currently-active stage. When ALL of a stage's objectives are done the stage
 * advances (its successor's progress is re-seeded); when the final stage clears the
 * quest is fully complete (stageIndex === stages.length) and reported in
 * `completed` — ready for a warm turn-in. One event only ever touches the stage that
 * was active when it fired (no cascade), keeping folds deterministic.
 */
export function advanceQuests(
  state: QuestState,
  event: QuestEvent,
  defs: readonly QuestDef[],
  lookup: QuestLookup,
): QuestAdvance {
  const defById = new Map(defs.map((d) => [d.id, d]));
  const stageCleared: { questId: string; stageIndex: number }[] = [];
  const completed: string[] = [];
  let changed = false;

  const nextActive = state.active.map((q): QuestProgress => {
    const def = defById.get(q.questId);
    // Unknown def, already turned in, or already fully complete → untouched.
    if (!def || q.turnedIn || q.stageIndex >= def.stages.length) return q;

    const stage = def.stages[q.stageIndex];
    if (!stage) return q;

    let objectivesChanged = false;
    const nextObjectives = q.objectives.map((op): ObjectiveProgress => {
      if (op.done) return op; // idempotent: a met objective never re-ticks
      const objDef = stage.objectives[op.index];
      if (!objDef) return op;
      const delta = eventDelta(objDef, event, lookup);
      if (delta == null) return op;
      const req = requiredCount(objDef);
      const count = Math.min(req, op.count + delta);
      objectivesChanged = true;
      return { index: op.index, count, done: count >= req };
    });

    if (!objectivesChanged) return q;
    changed = true;

    const stageDone = nextObjectives.every((op) => op.done);
    if (!stageDone) {
      return { ...q, objectives: nextObjectives };
    }

    // Stage cleared → advance.
    stageCleared.push({ questId: q.questId, stageIndex: q.stageIndex });
    const nextStageIndex = q.stageIndex + 1;
    if (nextStageIndex >= def.stages.length) {
      // Whole quest complete: ready to turn in (still lives in `active`).
      completed.push(q.questId);
      return { ...q, stageIndex: nextStageIndex, objectives: [] };
    }
    return {
      ...q,
      stageIndex: nextStageIndex,
      objectives: seedStage(def, nextStageIndex),
    };
  });

  const next: QuestState = changed ? { ...state, active: nextActive } : state;
  return { next, stageCleared, completed };
}

// ── offerableQuests ─────────────────────────────────────────────────────────────

/**
 * Is a quest's soft availability gate satisfied? Fail-open (never a hard wall).
 * `badge` / `quest-done` gates read the authoritative QuestState (badges + completed
 * ids live there) so the caller can't desync; `world` supplies external facts
 * (caught count, friendship) plus a fallback for the state-derived lists.
 */
function isAvailable(def: QuestDef, state: QuestState, world: QuestWorldSlice): boolean {
  const a = def.available;
  if (!a || a.kind === 'always') return true;
  switch (a.kind) {
    case 'badge': {
      const badges = state.badges.length ? state.badges : world.badges;
      return a.ref != null && badges.includes(a.ref);
    }
    case 'quest-done': {
      const done = state.completed.length ? state.completed : world.completedQuestIds;
      return a.ref != null && done.includes(a.ref);
    }
    case 'friendship': {
      const threshold = a.threshold ?? 0;
      const score = a.ref && world.friendshipWith ? world.friendshipWith(a.ref) : 0;
      return score >= threshold;
    }
    case 'caught-count':
      return world.caughtCount >= (a.threshold ?? 0);
    default:
      return true; // unknown gate → visible, never a locked door
  }
}

/**
 * Pure: which not-yet-started, not-completed quests are now OFFERABLE. Visibility
 * only — an un-offered quest is simply invisible, never a blocked path elsewhere.
 */
export function offerableQuests(
  state: QuestState,
  defs: readonly QuestDef[],
  world: QuestWorldSlice,
): readonly QuestDef[] {
  const activeIds = new Set(state.active.map((q) => q.questId));
  const doneIds = new Set(state.completed);
  return defs.filter(
    (d) => !activeIds.has(d.id) && !doneIds.has(d.id) && isAvailable(d, state, world),
  );
}

// ── lifecycle ─────────────────────────────────────────────────────────────────

/**
 * Pure: accept an offered quest → seed its QuestProgress at stage 0. Idempotent: if
 * the quest is already active or already completed, the state is returned unchanged.
 */
export function acceptQuest(state: QuestState, def: QuestDef, now: number): QuestState {
  if (state.active.some((q) => q.questId === def.id)) return state;
  if (state.completed.includes(def.id)) return state;
  const progress: QuestProgress = {
    questId: def.id,
    stageIndex: 0,
    objectives: seedStage(def, 0),
    startedAt: now,
    turnedIn: false,
  };
  return { ...state, active: [...state.active, progress] };
}

/**
 * Pure: turn in a finished quest → move it out of `active` and record its id in
 * `completed` (the availability gates + Done tab read this). Idempotent: already-
 * completed or unknown ids return the state unchanged. Badges are appended by the
 * SESSION reward path, not here.
 */
export function turnInQuest(state: QuestState, questId: string): QuestState {
  if (state.completed.includes(questId)) return state;
  if (!state.active.some((q) => q.questId === questId)) return state;
  return {
    ...state,
    active: state.active.filter((q) => q.questId !== questId),
    completed: [...state.completed, questId],
  };
}
